import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import {
  Angle,
  Euler,
  Matrix4,
  Point,
  Quaternion,
  Vector3,
} from '@vertexvis/geometry';
import { Color, Disposable } from '@vertexvis/utils';
import classNames from 'classnames';

import { readDOM, writeDOM } from '../../lib/stencil';
import { TransformController } from '../../lib/transforms/controller';
import { Drawable } from '../../lib/transforms/drawable';
import {
  AngleUnits,
  AngleUnitType,
  DistanceUnits,
  DistanceUnitType,
} from '../../lib/types';
import { ModifierKey } from '../../lib/types/keys';
import { focusInputElement } from './dom';
import {
  calculateNewRotationAngle,
  computeHandleDeltaTransform,
  computeInputDeltaTransform,
  computeInputDisplayValue,
  computeInputPosition,
  convertCanvasPointToWorld,
  convertPointToCanvas,
  PointAndPlacement,
} from './util';
import {
  TransformWidgetInput,
  TransformWidgetInputWrapper,
} from './viewer-transform-widget-components';
import { TransformWidget } from './widget';

@Component({
  tag: 'vertex-viewer-transform-widget',
  styleUrl: 'viewer-transform-widget.css',
  shadow: true,
})
export class ViewerTransformWidget {
  /**
   * An event that is emitted when the position of the widget changes.
   */
  @Event({ bubbles: true })
  public positionChanged!: EventEmitter<Vector3.Vector3 | undefined>;

  /**
   * An event that is emitted when the rotation of the widget changes.
   */
  @Event({ bubbles: true })
  public rotationChanged!: EventEmitter<Euler.Euler | undefined>;

  /**
   * An event that is emitted when the interaction has ended
   */
  @Event({ bubbles: true })
  public interactionEnded!: EventEmitter<Matrix4.Matrix4 | undefined>;

  /**
   * An event that is emitted an interaction with the widget has started
   */
  @Event({ bubbles: true })
  public interactionStarted!: EventEmitter<void>;

  /**
   * The viewer to connect to transforms. If nested within a <vertex-viewer>,
   * this property will be populated automatically.
   */
  @Prop()
  public viewer?: HTMLVertexViewerElement;

  /**
   * The starting position of this transform widget. This position will be updated
   * as transforms occur. Setting this value to `undefined` will remove the widget.
   */
  @Prop({ mutable: true })
  public position?: Vector3.Vector3;

  /**
   * The starting angle for the transform widget. This rotation will be updated
   * as the rotations occur.
   */
  @Prop({ mutable: true })
  public rotation?: Euler.Euler;

  /**
   * The controller that is responsible for performing transforms.
   */
  @Prop({ mutable: true })
  public controller?: TransformController;

  /**
   * Determines whether the x-rotation is disabled on the widget
   */
  @Prop({ mutable: true })
  public xRotationDisabled = false;

  /**
   * Determines whether the y-rotation is disabled on the widget
   */
  @Prop({ mutable: true })
  public yRotationDisabled = false;

  /**
   * Determines whether the z-rotation is disabled on the widget
   */
  @Prop({ mutable: true })
  public zRotationDisabled = false;

  /**
   * Determines whether the x-translation is disabled on the widget
   */
  @Prop({ mutable: true })
  public xTranslationDisabled = false;

  /**
   * Determines whether the y-translation is disabled on the widget
   */
  @Prop({ mutable: true })
  public yTranslationDisabled = false;

  /**
   * Determines whether the z-translation is disabled on the widget
   */
  @Prop({ mutable: true })
  public zTranslationDisabled = false;

  /**
   * Determines whether the xy-translation is disabled on the widget
   */
  @Prop({ mutable: true })
  public xyTranslationDisabled = false;

  /**
   * Determines whether the xz-translation is disabled on the widget
   */
  @Prop({ mutable: true })
  public xzTranslationDisabled = false;

  /**
   * Determines whether the yz-translation is disabled on the widget
   */
  @Prop({ mutable: true })
  public yzTranslationDisabled = false;

  /**
   * Whether to show inputs beside the widget handles when they are interacted with.
   * Defaults to `true`.
   */
  @Prop()
  public showInputs = true;

  /**
   * The unit to show for translation inputs. Defaults to `millimeters`.
   *
   * @see DistanceUnitType
   */
  @Prop()
  public distanceUnit: DistanceUnitType = 'millimeters';

  /**
   * The unit to show for rotation inputs. Defaults to `degrees`.
   *
   * @see AngleUnitType
   */
  @Prop()
  public angleUnit: AngleUnitType = 'degrees';

  /**
   * The number of decimal places to show in the input. Defaults to `1`.
   */
  @Prop()
  public decimalPlaces = 1;

  /**
   * When defined, the widget will snap to the degree of the nearest multiple of
   * the given number when the user is rotating with the widget and holding the
   * key defined by rotationSnapKey. Defaults to undefined.
   */
  @Prop()
  public rotationSnapDegrees?: number;

  /**
   * When rotationSnapDegrees is defined, the widget will snap to the degree of the
   * nearest multiple of the given number when the user is rotating with the widget
   * and holding the key defined here. Defaults to the shift key.
   */
  @Prop()
  public rotationSnapKey: ModifierKey = 'shift';

  /**
   * Scalar to increase the size of the translation handles of this widget.
   * Values above zero and below three will result in the best visual representation
   * of the handles, and values above three may produce unexpected results.
   *
   * Defaults to `1`.
   */
  @Prop()
  public translationHandleScalar = 1;

  /**
   * Scalar to increase the size of the rotation handles of this widget.
   * Values above zero and below three will result in the best visual representation
   * of the handles, and values above three may produce unexpected results.
   *
   * Defaults to `1`.
   */
  @Prop()
  public rotationHandleScalar = 1;

  /**
   * Specifies the frequency, in milliseconds, to update the transform while interacting with the widget.
   *
   * This delay is used to group events happening in quick succession and results in smoother
   * widget movement.
   */
  @Prop()
  public interactionThrottle = 75;

  /**
   * **EXPERIMENTAL.**
   *
   * Enables Command+Z and Control+Z keybindings to perform an undo of
   * the previous delta transformation.
   *
   * @see `EXPERIMENTAL_undo`
   */
  @Prop()
  public EXPERIMENTAL_undoKeybindings = false;

  /**
   * @internal
   * @ignore
   *
   * Visible for testing.
   */
  @Prop({ mutable: true })
  public hovered?: Drawable;

  @State()
  protected inputPosition?: PointAndPlacement;

  @State()
  protected inputValue = 0;

  @State()
  protected isEndingTransform = false;

  @State()
  protected inputShouldFocus = false;

  @Element()
  private hostEl!: HTMLElement;

  private startingTransform?: Matrix4.Matrix4;
  private currentTransform?: Matrix4.Matrix4;
  private dragStartTransform?: Matrix4.Matrix4;

  private xArrowColor: Color.Color | string = '#ea3324';
  private yArrowColor: Color.Color | string = '#4faf32';
  private zArrowColor: Color.Color | string = '#0000ff';
  private twoAxesSquareColor: Color.Color | string = '#e0e0e0';
  private hoveredColor: Color.Color | string = '#ffff00';

  private widget?: TransformWidget;
  private lastDragged?: Drawable;
  private dragging?: Drawable;
  private lastAngle = 0;
  private lastWorldPosition?: Vector3.Vector3;
  private lastInputValue?: number;

  private canvasBounds?: DOMRect;
  private inputBounds?: DOMRect;
  private canvasResizeObserver?: ResizeObserver;
  private inputResizeObserver?: ResizeObserver;
  private hostStyleObserver?: MutationObserver;
  private canvasRef?: HTMLCanvasElement;
  private inputWrapperRef?: HTMLDivElement;
  private inputRef?: HTMLInputElement;

  private hoveredChangeDisposable?: Disposable;

  private interactionTimer?: number;
  private lastMouseEvent?: PointerEvent;

  protected componentDidLoad(): void {
    window.addEventListener('pointermove', this.handlePointerMove);

    this.canvasResizeObserver = new ResizeObserver(this.handleResize);
    this.inputResizeObserver = new ResizeObserver(this.handleInputResize);
    this.hostStyleObserver = new MutationObserver(this.handleStyleChange);

    if (this.canvasRef != null) {
      this.canvasResizeObserver.observe(this.canvasRef);

      this.setupTransformWidget(this.canvasRef);
    }

    this.hostStyleObserver.observe(this.hostEl, {
      attributes: true,
      attributeFilter: ['style'],
    });

    this.handleViewerChanged(this.viewer, undefined);
    this.handleStyleChange();
  }

  protected disconnectedCallback(): void {
    window.removeEventListener('pointermove', this.handlePointerMove);

    this.canvasResizeObserver?.disconnect();
    this.inputResizeObserver?.disconnect();
    this.hostStyleObserver?.disconnect();

    this.hoveredChangeDisposable?.dispose();
    this.widget?.dispose();
  }

  protected componentDidRender(): void {
    if (this.inputShouldFocus && this.inputRef != null) {
      focusInputElement(this.inputRef);
      this.inputShouldFocus = false;
    }
  }

  /**
   * **EXPERIMENTAL.**
   *
   * Performs an undo of the most recent set of transform manipulations,
   * categorized by the last pause in interactivity (pointerup on a handle,
   * Enter press on an input, keyup on an input).
   *
   * Note that this *does not* work repeatedly. I.e. only one undo can be
   * performed, and once another set of transform manipulations is performed,
   * the history is lost.
   */
  @Method()
  public async EXPERIMENTAL_undo(): Promise<void> {
    const undoDelta = await this.controller?.EXPERIMENTAL_undoTransform();

    if (this.currentTransform != null && undoDelta != null) {
      this.currentTransform = Matrix4.multiply(
        undoDelta,
        this.currentTransform
      );

      this.widget?.updateTransform(this.currentTransform);

      this.updateInputPosition();
      this.updateInputValue();
      this.lastInputValue = this.inputValue;

      this.position = Vector3.fromMatrixPosition(this.currentTransform);
      this.rotation = Euler.fromRotationMatrix(this.currentTransform);
    }
  }

  /**
   * @ignore
   */
  @Watch('viewer')
  protected handleViewerChanged(
    newViewer?: HTMLVertexViewerElement,
    oldViewer?: HTMLVertexViewerElement
  ): void {
    oldViewer?.removeEventListener('frameDrawn', this.handleViewerFrameDrawn);
    oldViewer?.removeEventListener(
      'dimensionschange',
      this.handleViewerDimensionsChange
    );
    newViewer?.addEventListener('frameDrawn', this.handleViewerFrameDrawn);
    newViewer?.addEventListener(
      'dimensionschange',
      this.handleViewerDimensionsChange
    );

    if (newViewer?.stream != null) {
      this.controller?.dispose();
      this.controller = new TransformController(newViewer.stream);
    }
  }

  /**
   * @ignore
   */
  @Watch('xTranslationDisabled')
  @Watch('yTranslationDisabled')
  @Watch('zTranslationDisabled')
  @Watch('xyTranslationDisabled')
  @Watch('xzTranslationDisabled')
  @Watch('yzTranslationDisabled')
  @Watch('xRotationDisabled')
  @Watch('yRotationDisabled')
  @Watch('zRotationDisabled')
  protected handleDisabledPropertyChanged(): void {
    this.widget?.updateDisabledAxis({
      xRotation: this.xRotationDisabled,
      yRotation: this.yRotationDisabled,
      zRotation: this.zRotationDisabled,

      xTranslation: this.xTranslationDisabled,
      yTranslation: this.yTranslationDisabled,
      zTranslation: this.zTranslationDisabled,

      xyTranslation: this.xyTranslationDisabled,
      xzTranslation: this.xzTranslationDisabled,
      yzTranslation: this.yzTranslationDisabled,
    });
  }

  /**
   * @ignore
   */
  @Watch('translationHandleScalar')
  @Watch('rotationHandleScalar')
  protected handleTransformHandleScalarChanged(): void {
    const effectiveArrowScalar = this.getTranslationScalar();
    const effectiveSquareScalar = this.getRotationScalar();

    this.widget?.updateScalars({
      xTranslation: effectiveArrowScalar,
      yTranslation: effectiveArrowScalar,
      zTranslation: effectiveArrowScalar,
      xyTranslation: effectiveSquareScalar,
      xzTranslation: effectiveSquareScalar,
      yzTranslation: effectiveSquareScalar,
      xRotation: effectiveSquareScalar,
      yRotation: effectiveSquareScalar,
      zRotation: effectiveSquareScalar,
    });
  }

  /**
   * @ignore
   */
  @Watch('rotation')
  protected handleRotationChanged(
    newRotation?: Euler.Euler,
    oldRotation?: Euler.Euler
  ): void {
    const rotationToApply = newRotation ?? Euler.create();

    this.currentTransform = this.getTransformForNewRotation(rotationToApply);
    this.startingTransform = this.currentTransform;

    // If the removal of the previous rotation above results in an identity matrix,
    // clear the transformation on the widget to prevent it from appearing at the origin.
    if (
      newRotation == null &&
      this.currentTransform != null &&
      Matrix4.isIdentity(this.currentTransform)
    ) {
      this.currentTransform = undefined;
      this.startingTransform = this.currentTransform;
    }

    this.widget?.updateTransform(this.currentTransform);

    console.debug(
      `Updating widget rotation [previous=${JSON.stringify(
        oldRotation
      )}, current=${JSON.stringify(newRotation)}]`
    );

    this.rotationChanged.emit(newRotation);
  }

  /**
   * @ignore
   */
  @Watch('position')
  protected handlePositionChanged(
    newPosition?: Vector3.Vector3,
    oldPosition?: Vector3.Vector3
  ): void {
    this.currentTransform = this.getTransformForNewPosition(newPosition);
    this.startingTransform = this.currentTransform;

    console.debug(
      `Updating widget position [previous=${JSON.stringify(
        oldPosition
      )}, current=${JSON.stringify(newPosition)}]`
    );
    this.widget?.updateTransform(this.currentTransform);

    if (newPosition == null) {
      this.lastDragged = undefined;
      this.inputPosition = undefined;
      this.controller?.clearTransform();
    }

    this.positionChanged.emit(newPosition);
  }

  /**
   * @ignore
   */
  @Watch('distanceUnit')
  @Watch('angleUnit')
  @Watch('decimalPlaces')
  protected handleInputFormattingChanged(): void {
    this.updateInputValue();
    this.lastInputValue = this.inputValue;
  }

  public render(): h.JSX.IntrinsicElements {
    return (
      <Host>
        <canvas
          ref={(el) => {
            this.canvasRef = el;
          }}
          class={classNames('widget', {
            hovered: this.hovered != null,
          })}
          tabindex="0"
          width={this.viewer?.viewport.width}
          height={this.viewer?.viewport.height}
          onPointerDown={this.handleBeginDrag}
          onKeyDown={this.handleKeyDown}
        />

        {this.showInputs && this.inputPosition && this.viewer?.viewport && (
          <TransformWidgetInputWrapper
            ref={(el) => {
              if (el != null) {
                this.inputResizeObserver?.observe(el);
              } else if (this.inputRef != null) {
                this.inputResizeObserver?.unobserve(this.inputRef);
              }
              this.inputWrapperRef = el;
            }}
            bounds={this.inputBounds}
            viewport={this.viewer.viewport}
            point={this.inputPosition.point}
            placement={this.inputPosition.placement}
            displayUnit={this.getDisplayedUnits()}
          >
            <TransformWidgetInput
              ref={(el) => {
                this.inputRef = el;
              }}
              identifier={
                this.dragging?.identifier ?? this.lastDragged?.identifier
              }
              disabled={this.isEndingTransform}
              onChange={this.handleInputChange}
              onIncrement={() => this.handleInputStep(1)}
              onDecrement={() => this.handleInputStep(-1)}
              onBlur={() => {
                this.inputShouldFocus = false;
              }}
              onUndo={
                this.EXPERIMENTAL_undoKeybindings
                  ? () => this.EXPERIMENTAL_undo()
                  : undefined
              }
            />
          </TransformWidgetInputWrapper>
        )}
      </Host>
    );
  }

  private handleHoveredDrawableChanged = (drawable?: Drawable): void => {
    this.hovered = drawable;
  };

  private handleViewerFrameDrawn = (): void => {
    this.updatePropsFromViewer();
  };

  private handleViewerDimensionsChange = (): void => {
    writeDOM(() => {
      if (this.viewer != null && this.canvasRef != null) {
        this.canvasRef.width = this.viewer.viewport.width;
        this.canvasRef.height = this.viewer.viewport.height;

        this.updateCanvasBounds(this.canvasRef);
      }
    });
  };

  private handleResize = (): void => {
    if (this.canvasRef != null) {
      this.updateCanvasBounds(this.canvasRef);
    }
  };

  private handleInputResize = (): void => {
    if (this.inputWrapperRef != null) {
      const wrapperElement = this.inputWrapperRef;

      readDOM(() => {
        this.inputBounds = wrapperElement.getBoundingClientRect();
      });
    }
  };

  private handleStyleChange = (): void => {
    readDOM(() => {
      const hostStyles = window.getComputedStyle(this.hostEl);

      this.xArrowColor = hostStyles
        .getPropertyValue('--viewer-transform-widget-x-axis-arrow-color')
        .trim();
      this.yArrowColor = hostStyles
        .getPropertyValue('--viewer-transform-widget-y-axis-arrow-color')
        .trim();
      this.zArrowColor = hostStyles
        .getPropertyValue('--viewer-transform-widget-z-axis-arrow-color')
        .trim();
      this.twoAxesSquareColor = hostStyles
        .getPropertyValue('--viewer-transform-widget-two-axes-square-color')
        .trim();
      this.hoveredColor = hostStyles
        .getPropertyValue('--viewer-transform-widget-hovered-arrow-color')
        .trim();

      this.getTransformWidget().updateColors({
        xArrow: this.xArrowColor,
        yArrow: this.yArrowColor,
        zArrow: this.zArrowColor,
        twoAxesSquare: this.twoAxesSquareColor,
        hovered: this.hoveredColor,
      });
    });
  };

  private handlePointerMove = (event: PointerEvent): void => {
    if (this.dragging == null) {
      const canvasPoint = convertPointToCanvas(
        Point.create(event.clientX, event.clientY),
        this.getCanvasBounds()
      );
      const widget = this.getTransformWidget();

      if (canvasPoint != null && widget.boundsContainsPoint(canvasPoint)) {
        widget.updateCursor(canvasPoint);
      } else {
        widget.updateCursor(undefined);
        this.hovered = undefined;
      }
    }
  };

  private handleBeginDrag = async (event: PointerEvent): Promise<void> => {
    const canvasBounds = this.getCanvasBounds();

    if (
      this.hovered != null &&
      canvasBounds != null &&
      this.viewer != null &&
      this.position != null &&
      this.viewer.frame != null
    ) {
      this.dragging = this.hovered;

      if (this.dragging.identifier !== this.lastDragged?.identifier) {
        this.inputValue = 0;
        this.dragStartTransform = this.currentTransform;
        this.updateInputPosition();
        this.updateInputValue();
      }
      this.lastDragged = undefined;

      const currentCanvas = convertPointToCanvas(
        Point.create(event.clientX, event.clientY),
        canvasBounds
      );
      const widgetCenter = this.viewer.viewport.transformWorldToViewport(
        this.position,
        this.viewer.frame.scene.camera.projectionViewMatrix
      );

      this.lastAngle =
        currentCanvas != null
          ? Angle.fromPoints(widgetCenter, currentCanvas)
          : 0;

      this.lastWorldPosition = convertCanvasPointToWorld(
        currentCanvas,
        this.viewer?.frame,
        this.viewer?.viewport,
        this.currentTransform
      );

      this.interactionStarted.emit();

      window.removeEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointermove', this.handleDragWithTimer);
      window.addEventListener('pointerup', this.handleEndTransform);
    }
  };

  private handleDragWithTimer = async (event: PointerEvent): Promise<void> => {
    if (event != null) {
      // Prevent selection of text and interaction with view cube while dragging the widget
      event.preventDefault();

      this.lastMouseEvent = event;
    }

    if (this.interactionTimer == null) {
      this.interactionTimer = window.setTimeout(async () => {
        this.interactionTimer = undefined;
        await this.handleDrag();
        this.lastMouseEvent = undefined;
      }, this.interactionThrottle);
    }
  };

  private handleDrag = async (): Promise<void> => {
    const canvasBounds = this.getCanvasBounds();

    if (
      this.dragging != null &&
      this.lastWorldPosition != null &&
      canvasBounds != null &&
      this.viewer != null &&
      this.viewer.frame != null &&
      this.position != null &&
      this.lastMouseEvent != null
    ) {
      // Begin the transform on the first `pointermove` event as opposed to the
      // `pointerdown` to prevent accidental no-op transforms (identity matrix).
      await this.controller?.beginTransform();

      const currentCanvas = convertPointToCanvas(
        Point.create(this.lastMouseEvent.clientX, this.lastMouseEvent.clientY),
        canvasBounds
      );
      const widgetCenter = this.viewer.viewport.transformWorldToViewport(
        this.position,
        this.viewer.frame.scene.camera.projectionViewMatrix
      );

      const currentWorld = convertCanvasPointToWorld(
        currentCanvas,
        this.viewer?.frame,
        this.viewer?.viewport,
        this.currentTransform
      );

      if (
        currentWorld != null &&
        currentCanvas != null &&
        widgetCenter != null
      ) {
        const angle = Angle.fromPoints(widgetCenter, currentCanvas);
        const angleToUse = calculateNewRotationAngle(
          this.lastMouseEvent,
          this.rotationSnapKey,
          angle,
          this.lastAngle,
          this.getDisplayedAngle(),
          this.rotationSnapDegrees
        );

        this.transform(
          this.lastWorldPosition,
          currentWorld,
          angleToUse - this.lastAngle
        );

        this.updateInputPosition();
        this.updateInputValue();

        this.lastWorldPosition = currentWorld;
        this.lastAngle = angleToUse;
      }
    }
  };

  private handleEndTransform = async (event: PointerEvent): Promise<void> => {
    const canvasPoint = convertPointToCanvas(
      Point.create(event.clientX, event.clientY),
      this.getCanvasBounds()
    );
    const widget = this.getTransformWidget();
    this.lastDragged = this.dragging;

    this.dragging = undefined;
    this.lastWorldPosition = undefined;
    this.lastAngle = 0;

    this.updateInputPosition();
    this.updateInputValue();

    widget.updateCursor(canvasPoint);
    widget.updateTransform(this.currentTransform);

    this.beginEndTransform();

    window.removeEventListener('pointermove', this.handleDragWithTimer);
    window.removeEventListener('pointerup', this.handleEndTransform);

    try {
      const delta = this.controller?.getCurrentDelta();

      await this.controller?.endTransform();

      this.interactionEnded.emit(delta);
    } catch (e) {
      console.error('Failed to end transform interaction', e);
    }

    window.addEventListener('pointermove', this.handlePointerMove);

    this.completeEndTransform();
  };

  private handleInputStep = async (step: number): Promise<void> => {
    if (this.inputValue != null && this.lastInputValue != null) {
      // If modifying angle units, we want to adjust the step to always
      // perform the equivalent of a 1 degree rotation.
      if (this.isModifyingAngleUnits()) {
        const angles = new AngleUnits(this.angleUnit);
        const previous = angles.convertFrom(this.lastInputValue);
        const next = Angle.normalizeRadians(previous + Angle.toRadians(step));

        this.inputValue = angles.convertTo(next);
      } else {
        this.inputValue = this.lastInputValue + step;
      }
      await this.updateTransformFromInput(this.inputValue);
    }
  };

  private handleInputChange = async (value: number): Promise<void> => {
    await this.updateTransformFromInput(value);
  };

  private handleKeyDown = async (event: KeyboardEvent): Promise<void> => {
    const commandOrControlModifier = event.ctrlKey || event.metaKey;

    if (
      event.key === 'z' &&
      commandOrControlModifier &&
      this.EXPERIMENTAL_undoKeybindings
    ) {
      event.stopPropagation();
      await this.EXPERIMENTAL_undo();
    }
  };

  private updateTransformFromInput = async (value: number): Promise<void> => {
    if (
      this.lastDragged != null &&
      this.currentTransform != null &&
      this.startingTransform != null &&
      this.lastInputValue != null
    ) {
      this.currentTransform = computeInputDeltaTransform(
        this.currentTransform,
        this.lastDragged.identifier,
        value,
        this.lastInputValue,
        this.distanceUnit,
        this.angleUnit
      );

      this.updateInputValue();
      this.lastInputValue = value;

      await this.controller?.beginTransform();
      this.getTransformWidget().updateTransform(this.currentTransform);
      await this.controller?.updateTransform(
        Matrix4.multiply(
          this.currentTransform,
          Matrix4.invert(this.startingTransform)
        )
      );
      this.updateInputPosition();
      await this.controller?.endTransformDebounced(
        this.beginEndTransform,
        this.completeEndTransform
      );
    }
  };

  private updatePropsFromViewer = (): void => {
    const { frame } = this.viewer || {};

    if (frame != null) {
      const widget = this.getTransformWidget();

      widget.updateFrame(frame, this.dragging == null);
      this.updateInputPosition();
    }
  };

  private transform(
    previous: Vector3.Vector3,
    next: Vector3.Vector3,
    angle: number
  ): void {
    if (
      this.position != null &&
      this.startingTransform != null &&
      this.currentTransform != null &&
      this.dragging != null &&
      this.viewer != null &&
      this.viewer.frame != null
    ) {
      this.currentTransform = computeHandleDeltaTransform(
        this.currentTransform,
        previous,
        next,
        this.viewer?.frame.scene.camera.viewVector,
        angle,
        this.dragging.identifier
      );

      this.getTransformWidget().updateTransform(this.currentTransform);
      this.controller?.updateTransform(
        Matrix4.multiply(
          this.currentTransform,
          Matrix4.invert(this.startingTransform)
        )
      );
    }
  }

  private handleSettingDisabledAxis(): void {
    if (this.widget) {
      this.widget.updateDisabledAxis({
        xRotation: this.xRotationDisabled,
        yRotation: this.yRotationDisabled,
        zRotation: this.zRotationDisabled,
        xTranslation: this.xTranslationDisabled,
        yTranslation: this.yTranslationDisabled,
        zTranslation: this.zTranslationDisabled,
        xyTranslation: this.xyTranslationDisabled,
        xzTranslation: this.xzTranslationDisabled,
        yzTranslation: this.yzTranslationDisabled,
      });
    } else {
      console.warn('Cannot set disabled values - no widget defined');
    }
  }

  private setupTransformWidget = (
    canvasRef: HTMLCanvasElement
  ): TransformWidget => {
    console.debug(
      `Initializing transform widget. [initial-position=${JSON.stringify(
        this.position
      )}, has-initial-frame=${this.viewer?.frame != null}]`
    );

    const effectiveArrowScalar = this.getTranslationScalar();
    const effectiveSquareScalar = this.getRotationScalar();

    this.widget = new TransformWidget(canvasRef, {
      colors: {
        xArrow: this.xArrowColor,
        yArrow: this.yArrowColor,
        zArrow: this.zArrowColor,
        twoAxesSquare: this.twoAxesSquareColor,
        hovered: this.hoveredColor,
      },
      scalars: {
        xTranslation: effectiveArrowScalar,
        yTranslation: effectiveArrowScalar,
        zTranslation: effectiveArrowScalar,
        xyTranslation: effectiveSquareScalar,
        xzTranslation: effectiveSquareScalar,
        yzTranslation: effectiveSquareScalar,
        xRotation: effectiveSquareScalar,
        yRotation: effectiveSquareScalar,
        zRotation: effectiveSquareScalar,
      },
    });

    if (this.rotation != null) {
      this.currentTransform = this.getTransformForNewRotation(this.rotation);
      this.startingTransform = this.currentTransform;
      this.widget.updateTransform(this.currentTransform);
    }

    if (this.position != null) {
      this.currentTransform = this.getTransformForNewPosition(this.position);
      this.startingTransform = this.currentTransform;
      this.widget.updateTransform(this.currentTransform);
    }
    if (this.viewer?.frame != null) {
      this.widget.updateFrame(this.viewer.frame, true);
    }

    this.handleSettingDisabledAxis();

    this.hoveredChangeDisposable = this.widget.onHoveredChanged(
      this.handleHoveredDrawableChanged
    );

    return this.widget;
  };

  private updateCanvasBounds = (canvasElement: HTMLCanvasElement): void => {
    readDOM(() => {
      this.canvasBounds = canvasElement.getBoundingClientRect();

      this.getTransformWidget().updateDimensions(canvasElement);
    });
  };

  private updateInputValue = (): void => {
    const dragging = this.dragging ?? this.lastDragged;
    const isDraggingTwoAxesTranslation =
      dragging?.identifier === 'xy-translate' ||
      dragging?.identifier === 'xz-translate' ||
      dragging?.identifier === 'yz-translate';

    if (
      dragging != null &&
      this.currentTransform != null &&
      this.dragStartTransform != null &&
      !isDraggingTwoAxesTranslation
    ) {
      this.lastInputValue = this.inputValue;

      this.inputValue = computeInputDisplayValue(
        dragging.identifier,
        this.currentTransform,
        this.dragStartTransform,
        this.distanceUnit,
        this.angleUnit
      );

      if (this.inputRef != null) {
        const definedValue =
          this.getDisplayedDistance() ?? this.getDisplayedAngle() ?? 0;
        const displayValue = `${parseFloat(
          definedValue.toFixed(this.decimalPlaces)
        )}`;

        this.inputRef.value = displayValue;
      }
    }
  };

  private updateInputPosition = (): void => {
    const dragging = this.dragging ?? this.lastDragged;

    const isDraggingTwoAxesTranslation =
      dragging?.identifier === 'xy-translate' ||
      dragging?.identifier === 'xz-translate' ||
      dragging?.identifier === 'yz-translate';

    if (
      this.showInputs &&
      this.viewer?.frame != null &&
      this.position != null &&
      dragging != null &&
      !isDraggingTwoAxesTranslation
    ) {
      const widget = this.getTransformWidget();
      const widgetBounds = widget.getFullBounds();

      this.inputPosition =
        widgetBounds != null
          ? computeInputPosition(
              this.viewer.viewport,
              widgetBounds,
              dragging.points.toArray()
            )
          : undefined;
    } else if (isDraggingTwoAxesTranslation) {
      this.inputPosition = undefined;
    }
  };

  private beginEndTransform = (): void => {
    const widget = this.getTransformWidget();

    widget.updateDisabledAxis({
      xRotation: true,
      yRotation: true,
      zRotation: true,
      xTranslation: true,
      yTranslation: true,
      zTranslation: true,
      xyTranslation: true,
      xzTranslation: true,
      yzTranslation: true,
    });

    this.isEndingTransform = true;
  };

  private completeEndTransform = (): void => {
    const widget = this.getTransformWidget();

    widget.updateDisabledAxis({
      xRotation: this.xRotationDisabled,
      yRotation: this.yRotationDisabled,
      zRotation: this.zRotationDisabled,

      xTranslation: this.xTranslationDisabled,
      yTranslation: this.yTranslationDisabled,
      zTranslation: this.zTranslationDisabled,

      xyTranslation: this.xyTranslationDisabled,
      xzTranslation: this.xzTranslationDisabled,
      yzTranslation: this.yzTranslationDisabled,
    });

    this.isEndingTransform = false;
    this.inputShouldFocus = true;

    if (this.currentTransform != null) {
      this.position = Vector3.fromMatrixPosition(this.currentTransform);
      this.rotation = Euler.fromRotationMatrix(this.currentTransform);
    }
  };

  private isModifyingAngleUnits = (): boolean => {
    const draggingIdentifier =
      this.dragging?.identifier ?? this.lastDragged?.identifier;

    return !!draggingIdentifier?.includes('rotate');
  };

  private getTranslationScalar = (): number => {
    if (this.translationHandleScalar > 0) {
      return this.translationHandleScalar;
    }
    console.warn(
      'Invalid value provided for translation-handle-scalar. Expected a positive value greater than zero.'
    );
    return 1;
  };

  private getRotationScalar = (): number => {
    if (this.rotationHandleScalar > 0) {
      return this.rotationHandleScalar;
    }
    console.warn(
      'Invalid value provided for rotation-handle-scalar. Expected a positive value greater than zero.'
    );
    return 1;
  };

  private getDisplayedUnits = (): AngleUnits | DistanceUnits => {
    if (this.isModifyingAngleUnits()) {
      return new AngleUnits(this.angleUnit);
    }
    return new DistanceUnits(this.distanceUnit);
  };

  private getDisplayedAngle = (): number | undefined => {
    if (this.isModifyingAngleUnits()) {
      return this.inputValue;
    }
  };

  private getDisplayedDistance = (): number | undefined => {
    const draggingIdentifier =
      this.dragging?.identifier ?? this.lastDragged?.identifier;
    if (draggingIdentifier?.includes('translate')) {
      return this.inputValue;
    }
  };

  private getTransformForNewPosition = (
    newPosition?: Vector3.Vector3
  ): Matrix4.Matrix4 | undefined => {
    if (newPosition != null) {
      const c =
        this.currentTransform != null
          ? this.currentTransform
          : Matrix4.makeIdentity();

      const currentRotation = Matrix4.makeRotation(
        Quaternion.fromMatrixRotation(c)
      );
      const position = Matrix4.makeTranslation(newPosition);

      return Matrix4.multiply(position, currentRotation);
    }
  };

  private getTransformForNewRotation = (
    newRotationEuler: Euler.Euler
  ): Matrix4.Matrix4 | undefined => {
    const c =
      this.currentTransform != null
        ? this.currentTransform
        : Matrix4.makeIdentity();

    const oldRotation = Matrix4.invert(
      Matrix4.makeRotation(Quaternion.fromMatrixRotation(c))
    );

    const newRotation = Matrix4.makeRotation(
      Quaternion.fromEuler(newRotationEuler)
    );
    const oldTranslation = Matrix4.multiply(c, oldRotation);

    return Matrix4.multiply(oldTranslation, newRotation);
  };

  private getCanvasBounds = (): DOMRect | undefined => {
    if (this.canvasBounds != null) {
      return this.canvasBounds;
    } else if (this.canvasRef != null) {
      this.updateCanvasBounds(this.canvasRef);
      return this.canvasBounds;
    }
  };

  private getTransformWidget = (): TransformWidget => {
    if (this.widget == null && this.canvasRef != null) {
      return this.setupTransformWidget(this.canvasRef);
    } else if (this.widget != null) {
      return this.widget;
    } else {
      throw new Error(
        'Transform widget was not initialized. The canvas element may not have been initialized.'
      );
    }
  };
}
